import { Companion, BookingTier } from '../types';
import { getCompanionRateForService } from './companionServiceMatcher';
import { calculatePayout } from './feeCalculator';

export const MIN_HOURLY_RATE = 600;

export interface BookingPricingResult {
  bookingTier: BookingTier;
  durationHours: number;
  packageTitle: string;
  baseAmount: number;
  hourlyRate: number;
  rateOneThirdDay: number;
  rateHalfDay: number;
  rateFullDay: number;
  platformFee: number; // 15%
  companionPayout: number; // 85%
  taxes: number; // 5% GST
  totalPayable: number;
  calculationBreakdown: string;
}

/**
 * Returns default package rates based on a companion's hourly rate (minimum ₹600).
 */
export function getDefaultPackageRates(hourlyRate: number = 600): {
  rateOneThirdDay: number;
  rateHalfDay: number;
  rateFullDay: number;
} {
  const safeHourly = Math.max(MIN_HOURLY_RATE, Math.round(hourlyRate || MIN_HOURLY_RATE));
  return {
    rateOneThirdDay: Math.max(2000, Math.round(safeHourly * 6 * 0.85)), // 6-hour package
    rateHalfDay: Math.max(3000, Math.round(safeHourly * 12 * 0.80)), // 12-hour package
    rateFullDay: Math.max(5000, Math.round(safeHourly * 24 * 0.75)), // 24-hour package
  };
}

/**
 * Authoritative pricing calculation adhering to PairUpToday Duration & Pricing Structure:
 * 1. Hourly Booking: min ₹600/hr, rate = hourlyRate * hours
 * 2. 1/3 Day Booking (6 hours duration): fixed rateOneThirdDay
 * 3. Half-Day Booking (12 hours duration): fixed rateHalfDay
 * 4. Full-Day Booking (24 hours duration): fixed rateFullDay
 * 5. 85% Companion Payout / 15% Platform Fee
 */
export function calculateBookingPricing(
  companion: Companion,
  bookingTier: BookingTier,
  customDurationHours?: number,
  serviceName?: string
): BookingPricingResult {
  const rawHourly = serviceName
    ? getCompanionRateForService(companion, serviceName)
    : (companion.hourlyRate || MIN_HOURLY_RATE);
  const hourlyRate = Math.max(MIN_HOURLY_RATE, Math.round(rawHourly));

  const defaults = getDefaultPackageRates(hourlyRate);
  const rateOneThirdDay = companion.rateOneThirdDay || defaults.rateOneThirdDay;
  const rateHalfDay = companion.rateHalfDay || defaults.rateHalfDay;
  const rateFullDay = companion.rateFullDay || defaults.rateFullDay;

  let baseAmount = 0;
  let durationHours = 0;
  let packageTitle = '';
  let calculationBreakdown = '';

  switch (bookingTier) {
    case 'hourly': {
      durationHours = Math.max(1, Math.min(23, customDurationHours || 2));
      baseAmount = hourlyRate * durationHours;
      packageTitle = `Hourly Booking (${durationHours} Hour${durationHours > 1 ? 's' : ''})`;
      calculationBreakdown = `₹${hourlyRate}/hr × ${durationHours} hrs`;
      break;
    }
    case 'one_third_day': {
      // 6 hours package duration
      durationHours = customDurationHours && customDurationHours > 0 && customDurationHours <= 6 ? customDurationHours : 6;
      baseAmount = rateOneThirdDay;
      packageTitle = '1/3 Day Package (6 Hours)';
      calculationBreakdown = `Fixed 1/3 Day Package rate for up to 6 hours (applied for ${durationHours} hrs)`;
      break;
    }
    case 'half_day': {
      // 12 hours package duration
      durationHours = customDurationHours && customDurationHours > 6 && customDurationHours <= 12 ? customDurationHours : 12;
      baseAmount = rateHalfDay;
      packageTitle = 'Half-Day Package (12 Hours)';
      calculationBreakdown = `Fixed Half-Day Package rate for 6–12 hours (applied for ${durationHours} hrs)`;
      break;
    }
    case 'full_day': {
      // 24 hours package duration
      durationHours = customDurationHours && customDurationHours > 12 && customDurationHours <= 24 ? customDurationHours : 24;
      baseAmount = rateFullDay;
      packageTitle = 'Full-Day Package (24 Hours)';
      calculationBreakdown = `Fixed Full-Day Package rate for 12–24 hours (applied for ${durationHours} hrs)`;
      break;
    }
  }

  const { platformFee, companionPayout } = calculatePayout(baseAmount);
  const taxes = Math.round(baseAmount * 0.05); // 5% GST
  const totalPayable = baseAmount + taxes;

  return {
    bookingTier,
    durationHours,
    packageTitle,
    baseAmount,
    hourlyRate,
    rateOneThirdDay,
    rateHalfDay,
    rateFullDay,
    platformFee,
    companionPayout,
    taxes,
    totalPayable,
    calculationBreakdown,
  };
}
